import { createContext, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock, Gift } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { AddressAutocomplete } from "./Autocomplete";
import { useAuth } from "../context/AuthContext";
import { useCountry } from "../context/CountryContext";
import { api, errMsg, money } from "../lib/api";

export const PaymentMethods = ({ country, value, onChange }) => (
  <RadioGroup value={value} onValueChange={onChange} className="grid gap-2 sm:grid-cols-2" data-testid="payment-methods">
    {country.payment_methods.map((m) => (
      <Label key={m.id} htmlFor={`pm-${m.id}`} data-testid={`payment-method-option-${m.id}`}
        className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition-colors ${value === m.id ? "border-primary bg-accent" : "bg-white hover:bg-muted"}`}>
        <RadioGroupItem value={m.id} id={`pm-${m.id}`} />
        <span className="text-sm font-medium">{m.label}</span>
      </Label>
    ))}
  </RadioGroup>
);

const Ctx = createContext(null);

const BuyDialog = ({ item, onClose }) => {
  const { country } = useCountry();
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [method, setMethod] = useState("card");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState(user?.phone || "");
  const [freeName, setFreeName] = useState("");
  const [busy, setBusy] = useState(false);
  if (!item || !country) return null;
  const isPkg = item.kind === "package";
  const bundle = isPkg || item.kind === "hosting";
  const freeTld = country.free_tld;
  const pay = async () => {
    if (!user) {
      sessionStorage.setItem("ac_pending_buy", JSON.stringify(item));
      onClose();
      return nav(`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`);
    }
    setBusy(true);
    const items = [{ kind: item.kind, package_id: item.package_id, plan_id: item.plan_id, domain: item.domain }];
    const label = freeName.trim().toLowerCase().replace(/[^a-z0-9-]/g, "");
    if (bundle && label && freeTld) items.push({ kind: "domain", domain: label + freeTld });
    try {
      const { data } = await api.post("/checkout", { country: country.code, payment_method: method, origin_url: window.location.origin, install_address: address, phone, items });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto rounded-3xl" data-testid="buy-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{item.name}</DialogTitle></DialogHeader>
        <p className="-mt-2 text-sm text-muted-foreground" data-testid="buy-dialog-price">
          {money(item.price, country.currency)}{item.kind === "domain" ? " for 1 year" : " per month"}
          {item.setup_fee > 0 && ` + ${money(item.setup_fee, country.currency)} once-off setup`}
        </p>
        {isPkg && (
          <div className="space-y-3">
            <div><Label>Installation address</Label><div className="mt-1.5 flex"><AddressAutocomplete value={address} onChange={setAddress} testid="buy-address-input" placeholder="Start typing your street address" className="h-11 rounded-xl" /></div></div>
            <div><Label>Phone</Label><Input className="mt-1.5 h-11 rounded-xl" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+27 …" data-testid="buy-phone-input" /></div>
          </div>
        )}
        {bundle && freeTld && (
          <div className="rounded-2xl bg-[#DDF7EE] p-3" data-testid="buy-free-domain">
            <Label className="flex items-center gap-1.5 text-[#0B7A6F]"><Gift className="h-4 w-4" />Claim your free {freeTld} domain (optional)</Label>
            <div className="mt-2 flex items-center rounded-xl border bg-white pr-3">
              <input className="h-10 min-w-0 flex-1 bg-transparent px-3 font-mono-data text-sm outline-none" value={freeName} onChange={(e) => setFreeName(e.target.value)} placeholder="yourname" data-testid="buy-free-domain-input" />
              <span className="font-mono-data text-sm text-muted-foreground">{freeTld}</span>
            </div>
          </div>
        )}
        <div><Label className="mb-2 block">Pay with</Label><PaymentMethods country={country} value={method} onChange={setMethod} /></div>
        {method !== "card" && bundle && <p className="text-xs text-muted-foreground">Monthly renewals for this method are paid from a "Pay now" link in your portal.</p>}
        {!country.flw_supported && <p className="text-xs text-amber-700">Cards are charged in USD; {country.currency} shown for reference.</p>}
        <Button className="h-12 w-full rounded-full text-base font-bold" onClick={pay} disabled={busy} data-testid="buy-pay-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Lock className="mr-2 h-4 w-4" />{user ? `Pay ${money(item.price + (item.setup_fee || 0), country.currency)}` : "Sign in to pay"}</>}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export const BuyProvider = ({ children }) => {
  const [item, setItem] = useState(null);
  const { user } = useAuth();
  useEffect(() => {
    const p = sessionStorage.getItem("ac_pending_buy");
    if (user && p) { sessionStorage.removeItem("ac_pending_buy"); setItem(JSON.parse(p)); }
  }, [user]);
  return (
    <Ctx.Provider value={{ buy: setItem }}>
      {children}
      {item && <BuyDialog item={item} onClose={() => setItem(null)} />}
    </Ctx.Provider>
  );
};

export const useBuy = () => useContext(Ctx);
